import { GivelifyIcon } from '@givelify/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { CloseButton, StyledModal } from './style';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Features } from 'store/featureFlags/types';
import { AppState } from 'store';
import { useWebSocket } from 'pages/signup/utils/useWebSocket';
import { webConfig } from 'webConfig';
import { onMessage } from 'pages/signup/utils/organizationAutoCompleteUtils';
import { OrganizationSuggestionsResponse } from 'store/signup/types';
import { NoSuggestions } from './NoSuggestions';
import { ResultsList } from './ResultsList';
import ClaimOrganizationModal from '../ClaimOrganizationModal';
import { RequestState } from 'pages/signup/utils/useRequestState';
import { signSetAction } from 'store/signup/actions';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes';
import { setSelectedOrganization } from 'pages/signup/utils/selectedOrganizationUtils';
import AlreadyRegistered from './AlreadyRegistered';
import { Skeleton } from '@mui/material';

type SuggestionsListProps = {
    open: boolean;
    onClose: () => void;
    reOpen: () => void;
};

export const SuggestionsList: React.FC<SuggestionsListProps> = ({
    open,
    onClose,
    reOpen,
}) => {
    const dispatch = useAppDispatch();
    const push = useNavigate();
    const { organizationName, organizationLocation, v2SearchAPIEnabled } =
        useSelector((state: AppState) => ({
            organizationName: state.Signup.organizationName,
            organizationLocation: state.Signup.organizationLocation,
            v2SearchAPIEnabled: state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }));
    const [loading, setLoading] = useState<boolean>(false);
    const [suggestionsState, makeRequest] = useWebSocket(
        v2SearchAPIEnabled
            ? webConfig.newOrganizationSearchUrl
            : webConfig.organizationSearchUrl,
        (
            res,
            dispatch: React.Dispatch<
                React.SetStateAction<
                    RequestState<OrganizationSuggestionsResponse[]>
                >
            >,
        ) => onMessage(res, dispatch, 15),
    );
    const [list, setList] = useState<OrganizationSuggestionsResponse[]>([]);
    const [claimOrgOpen, setClaimOrgOpen] = useState<boolean>(false);
    const [selectedOrg, setSelectedOrg] =
        useState<OrganizationSuggestionsResponse | null>(null);
    useEffect(() => {
        setList([]);
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }
        setLoading(true);
        makeRequest(organizationName, organizationLocation, undefined);
    }, [organizationName, organizationLocation, makeRequest, open]);

    useEffect(() => {
        if (suggestionsState.type === 'REQUEST_SUCCESS') {
            setList(suggestionsState.data);
            setLoading(false);
        }
    }, [suggestionsState]);

    const handleSelect = useCallback((data: OrganizationSuggestionsResponse) => {
        setSelectedOrg(data);
        dispatch(
            signSetAction({
                selectedOrganization: data,
            }),
        );
        setSelectedOrganization(data);

        const isUnregistered = data.source.unregistered === '1';
        if (isUnregistered) {
            onClose();

            push(PATH.SIGNUP.STEPS.CONFIRM_ORGANIZATION);
        } else {
            setShowAlreadyRegistered(true);
        }
    }, [dispatch, onClose, push]);

    const handleClaimClose = useCallback(() => {
        setClaimOrgOpen(false);
        reOpen();
    }, [reOpen]);

    const [showAlreadyRegistered, setShowAlreadyRegistered] = useState(false);

    if (showAlreadyRegistered)
        return (
            <AlreadyRegistered
                onBackClick={() => {
                    setShowAlreadyRegistered(false);
                }}
            />
        );

    return (
        <>
            <StyledModal open={open}>
                <CloseButton
                    aria-label="Close Dialog"
                    name="Close"
                    size="small"
                    variant="icon"
                    onClick={onClose}
                >
                    <GivelifyIcon variant="close-outlined" />
                </CloseButton>
                {loading ? (
                    <Skeleton width="100%" height="500px" animation="wave" />
                ) : list.length ? (
                    <ResultsList onSelect={handleSelect} list={list} />
                ) : (
                    <NoSuggestions />
                )}
            </StyledModal>
            {selectedOrg && (
                <ClaimOrganizationModal
                    isOpen={claimOrgOpen}
                    organization={selectedOrg}
                    handleClose={handleClaimClose}
                />
            )}
        </>
    );
};
